import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, Eye, MoreHorizontal, Repeat2, MessageSquareQuote, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { Loop, useLoopActions } from '@/hooks/use-loops';
import { cn } from '@/lib/utils';
import { getThumbnailUrl } from '@/utils/imageUtils';
import { getDarkGradient, getStringHash } from '@/utils/anonymousAvatars';
import LoopComments from './LoopComments';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useReblogActions, ReblogAnnotation } from '@/hooks/use-reblogs';
import TrailLensDialog from './TrailLensDialog';
import { useLoopCommunities, useCommunityInteraction } from '@/hooks/use-loop-communities';
import { CommunityBadges } from './CommunityBadges';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useAuth } from '@/contexts/AuthContext';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import AmbientBackdrop from '@/components/common/AmbientBackdrop';
import { MentionText } from '@/components/mentions/MentionText';
import { useMatureContent } from '@/hooks/use-mature-content';
import { AnnotationPinsOverlay } from '@/components/annotations/AnnotationPinsOverlay';

interface LoopCardProps {
  loop: Loop;
  onEngage?: (loopId: string) => void;
  isEngaged?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  annotations?: ReblogAnnotation;
}

// Helper to split content into title and description
const splitContent = (content: string) => {
  const parts = content?.split('\n\n') || [];
  return {
    title: parts[0] || '',
    description: parts.slice(1).join('\n\n')
  };
};

const truncateByWords = (text: string, maxWords: number) => {
  if (!text) return { truncated: text, isTruncated: false };

  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) {
    return { truncated: text, isTruncated: false };
  }

  const truncatedWords = words.slice(0, maxWords);
  return {
    truncated: `${truncatedWords.join(' ')}…`,
    isTruncated: true
  };
};

const LoopCard = ({ 
  loop, 
  onEngage, 
  isEngaged = false, 
  className,
  size = 'medium',
  annotations
}: LoopCardProps) => {
  const [showComments, setShowComments] = useState(false);
  const { likeLoop, unlikeLoop, viewLoop, isLiking, isUnliking, deleteLoop } = useLoopActions();
  const { reblog, quote, remix, isReblogging } = useReblogActions();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [trailOpen, setTrailOpen] = useState(false);
  const [showMature, setShowMature] = useState(false); // Per-post mature reveal
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  
  // Use mature content hook for consistency
  const { showMatureContent } = useMatureContent();
  
  // Fetch communities for this loop
  const { data: loopCommunities } = useLoopCommunities(loop.id);
  const { trackInteraction } = useCommunityInteraction();
  
  // Precompute anonymous background gradient to avoid calling hooks inside conditional branches
  const anonBg = useMemo(() => getDarkGradient(getStringHash(loop.id || 'anonymous')), [loop.id]);
  const canDelete = useMemo(() => {
    if (!user) return false;
    return user.id === loop.user_id || !!profile?.is_admin;
  }, [user, profile?.is_admin, loop.user_id]);
  
  // Check if mature content should be blurred
  const shouldBlurMature = loop.is_mature && !showMatureContent && !showMature;
  
  useEffect(() => {
    // Track view when card is rendered
    if (!loop.is_viewed) {
      viewLoop(loop.id);
    }
  }, [loop.id, loop.is_viewed, viewLoop]);
  
  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Engage the loop (reveal author) when liked
    if (!isEngaged && onEngage) {
      onEngage(loop.id);
    }
    
    if (loop.is_liked) {
      unlikeLoop(loop.id);
    } else {
      likeLoop(loop.id);
    }
  };
  
  // Only reveal author on like, not on click
  const handleEngage = () => {
    // Click doesn't reveal author anymore
    // We'll still call onEngage for any other functionality that might depend on it
    if (onEngage && !isEngaged) {
      onEngage(loop.id);
    }
  };
  
  const handleCommentsToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEngaged) {
      setShowComments(!showComments);
    } else {
      handleEngage();
    }
  };

  const formatCount = (n: number | undefined) => {
    const v = n ?? 0;
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}m`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return String(v);
  };
  
  const sizeClasses = {
    small: 'h-[200px] w-full',
    medium: 'h-[300px] w-full',
    large: 'h-[400px] w-full'
  };
  const createdAtDate = useMemo(() => {
    const d = new Date(loop.created_at);
    return isNaN(d.getTime()) ? null : d;
  }, [loop.created_at]);

  // Twemoji pixelated helper
  const emojiToCodepoints = (emoji: string) => Array.from(emoji || '').map(c => c.codePointAt(0)!.toString(16)).join('-');
  const twemojiUrl = (emoji?: string) => emoji ? `https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/72x72/${emojiToCodepoints(emoji)}.png` : '';

  // Fetch adopted community for this author if they have one
  const adoptedCommunityId = (loop.user as any)?.community_tag_id as string | undefined;
  const { data: adoptedCommunity } = useQuery({
    queryKey: ['adopted-community', adoptedCommunityId],
    enabled: !!adoptedCommunityId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('id, name, short_tag, emote')
        .eq('id', adoptedCommunityId!)
        .single();
      if (error) throw error;
      return data as { id: string; name: string; short_tag?: string | null; emote?: string | null };
    }
  });
  
  const revealAuthor = isEngaged || (user?.id === loop.user_id);

  return (
    <motion.div
      layout
      className={cn(
        'relative bg-card rounded-xl border shadow-sm overflow-hidden',
        'cursor-pointer transition-all duration-200',
        'hover:shadow-md hover:scale-[1.02]',
        sizeClasses[size],
        className
      )}
      onClick={handleEngage}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Media Content - Only show if image or video exists */}
      {(loop.image_url || loop.video_url) ? (
        <div className="relative h-3/5 bg-muted overflow-hidden rounded-t-xl">
          <AnnotationPinsOverlay postId={loop.id} postType="loop" />
          {/* Ambient backdrop */}
          <AmbientBackdrop imageUrl={loop.image_url || loop.thumbnail_url || undefined} />
          {/* Foreground media */}
          {loop.image_url ? (
            <img 
              src={loop.image_url} 
              alt="Loop media"
              className={cn(
                "relative block w-full h-full object-contain z-[1] border-0 outline-none ring-0 transition-all duration-200",
                shouldBlurMature && "blur-2xl"
              )}
            />
          ) : loop.video_url ? (
            <video 
              src={loop.video_url}
              poster={loop.thumbnail_url}
              className={cn(
                "relative block w-full h-full object-contain z-[1] border-0 outline-none ring-0 transition-all duration-200",
                shouldBlurMature && "blur-2xl"
              )}
              muted
              playsInline
            />
          ) : null}
          {/* View count overlay */}
          <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 z-[2]">
            <Eye size={12} />
            {isEngaged ? loop.view_count : <span className="w-3 h-3 bg-white/30 rounded-full"></span>}
          </div>
          {/* Mature content overlay */}
          {shouldBlurMature && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-[2]">
              <div className="text-center space-y-2 p-3">
                <p className="text-white font-medium text-sm">Sensitive Content</p>
                <p className="text-gray-300 text-xs max-w-[200px]">This post may contain mature or NSFW content</p>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMature(true);
                  }}
                  variant="outline"
                  size="sm"
                  className="bg-white/10 hover:bg-white/20 border-white/20 text-white text-xs"
                >
                  Show Content
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="relative px-4 pt-4">
          {/* View count for text-only posts */}
          <div className="absolute top-2 right-2 bg-black/20 text-foreground px-2 py-1 rounded-full text-xs flex items-center gap-1">
            <Eye size={12} />
            {isEngaged ? loop.view_count : <span className="w-3 h-3 bg-foreground/30 rounded-full"></span>}
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className={cn(
        "p-4 flex flex-col", 
        (loop.image_url || loop.video_url) ? "h-2/5" : "h-[calc(100%-40px)]"
      )}>
        {/* Reblog banner */}
        {annotations?.rebloggedBy && (
          <div className="-mt-1 mb-1 text-[11px] text-muted-foreground flex items-center gap-1">
            <Repeat2 size={12} />
            <span>
              Reblogged by <span className="font-medium">@{annotations.rebloggedBy.username}</span>
            </span>
          </div>
        )}
        {/* Author info - show actual info if engaged OR it's your own post; otherwise skeleton */}
        <AnimatePresence>
          {revealAuthor ? (
            <motion.div
              initial={{ opacity: 0, height: 'auto' }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-2 mb-2 flex-wrap"
            >
              <Avatar className={cn(
                "w-6 h-6",
                (loop.user as any)?.is_admin ? "ring-2 ring-[#F97316]" : (loop.user as any)?.is_premium ? "ring-2 ring-[#E5DEFF]" : "ring-1 ring-white/10"
              )}>
                <AvatarImage src={getThumbnailUrl(loop.user.profile_image_url, 64)} />
                <AvatarFallback className="text-xs">
                  {loop.user.display_name?.[0] || loop.user.username?.[0] || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium flex items-center gap-1.5">
                @{loop.user.username}
                {loop.user?.is_admin && (
                  <VerifiedBadge type="admin" size={18} className="inline-block" />
                )}
                {loop.user?.is_partner && (
                  <VerifiedBadge type="partner" size={18} className="inline-block" />
                )}
              </span>
              {adoptedCommunity ? (
                <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] leading-none">
                  {adoptedCommunity.emote ? (
                    <img src={twemojiUrl(adoptedCommunity.emote)} alt={adoptedCommunity.emote || ''}
                         className="h-3.5 w-3.5" style={{ imageRendering: 'pixelated' as any }} />
                  ) : null}
                  <span className="font-mono tracking-widest">{(adoptedCommunity.short_tag || adoptedCommunity.name || '').toUpperCase()}</span>
                </span>
              ) : (
                (loop.user as any)?.tag_code ? (
                  <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] leading-none">
                    {(loop.user as any)?.tag_emote ? (
                      <img src={twemojiUrl((loop.user as any).tag_emote)} alt={(loop.user as any).tag_emote}
                           className="h-3.5 w-3.5" style={{ imageRendering: 'pixelated' as any }} />
                    ) : null}
                    <span className="font-mono tracking-widest">{(loop.user as any).tag_code}</span>
                  </span>
                ) : null
              )}
              {/* Community badges inline */}
              {loopCommunities && loopCommunities.length > 0 && (
                <CommunityBadges 
                  communities={loopCommunities
                    .map(lc => lc.community)
                    .filter((c): c is NonNullable<typeof c> => c != null)
                  }
                  variant="inline"
                  onCommunityClick={(community) => {
                    // Track interaction and navigate to community
                    trackInteraction(community.id, 'view');
                    navigate(`/community/${community.name}`);
                  }}
                />
              )}
              <span className="text-xs text-muted-foreground ml-auto">
                {createdAtDate ? formatDistanceToNow(createdAtDate, { addSuffix: true }) : 'recently'}
              </span>
            </motion.div>
          ) : (
            <motion.div 
              className="flex items-center gap-2 mb-2" 
              initial={{ opacity: 0.7 }}
              animate={{ opacity: 0.8 }}
            >
              <div 
                className="w-6 h-6 rounded-full border border-border/40 flex items-center justify-center overflow-hidden"
                style={{ background: anonBg }}
              >
                <span className="text-xs text-zinc-300/60 opacity-70"></span>
              </div>
              <div className="relative overflow-hidden w-20 h-4 text-sm text-muted-foreground opacity-60">
                <motion.div
                  initial={{ opacity: 0.4 }}
                  animate={{
                    opacity: [0.4, 0.6, 0.4],
                    x: [-1, 1, -1],
                    transition: { repeat: Infinity, duration: 1.5 }
                  }}
                >
                  <span className="font-mono blur-[1.5px] tracking-widest">anonymous</span>
                </motion.div>
              </div>
              <div className="ml-auto text-xs text-muted-foreground opacity-60 blur-[1px]">recent</div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Loop content */}
        <div className="flex-1 overflow-hidden">
          {(() => {
            const { title, description } = splitContent(loop.content);
            const targetText = description || title;
            const { truncated, isTruncated } = truncateByWords(targetText, 200);

            if (description) {
              const { truncated: truncatedDescription, isTruncated: descTruncated } = truncateByWords(description, 200);
              return (
                <>
                  <h3 className={cn(
                    'font-semibold mb-1',
                    (loop.image_url || loop.video_url) ? 'text-base line-clamp-2' : 'text-lg line-clamp-2'
                  )}>
                    {title}
                  </h3>
                  <p className={cn(
                    'text-xs text-gray-400 whitespace-pre-line sm:text-sm'
                  )}>
                    {truncatedDescription}
                    {descTruncated && (
                      <button
                        type="button"
                        className="ml-1 text-primary underline-offset-2 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEngage();
                        }}
                      >
                        Read more
                      </button>
                    )}
                  </p>
                </>
              );
            }

            return (
              <p className={cn(
                'overflow-hidden whitespace-pre-line',
                (loop.image_url || loop.video_url) ? 'text-xs sm:text-sm' : 'text-sm sm:text-base font-medium'
              )}>
                {truncated}
                {isTruncated && (
                  <button
                    type="button"
                    className="ml-1 text-primary underline-offset-2 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEngage();
                    }}
                  >
                    Read more
                  </button>
                )}
              </p>
            );
          })()}
        </div>
        
        {/* Actions - show actual buttons if engaged, otherwise skeletons */}
        <div className="mt-3 pt-2 border-t">
          <AnimatePresence>
            {isEngaged ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'p-1 h-auto',
                      loop.is_liked && 'text-red-500'
                    )}
                    onClick={handleLike}
                    disabled={isLiking || isUnliking}
                  >
                    <Heart 
                      size={16} 
                      className={loop.is_liked ? 'fill-current' : ''} 
                    />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 h-auto"
                    onClick={handleCommentsToggle}
                  >
                    <MessageCircle size={16} />
                  </Button>

                  {/* Reblog actions */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 h-auto"
                    onClick={(e) => { e.stopPropagation(); reblog(loop.id); }}
                    disabled={isReblogging}
                  >
                    <Repeat2 size={16} />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 h-auto"
                    onClick={(e) => { e.stopPropagation(); setQuoteOpen(true); }}
                  >
                    <MessageSquareQuote size={16} />
                  </Button>

                  {/* Remix removed */}
                </div>
                
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={(e) => { e.stopPropagation(); }}>
                      <MoreHorizontal size={16} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" side="bottom" sideOffset={8} className="w-auto p-0 bg-[#272729] border-[#343536] text-[#D7DADC] z-[9999]" onClick={(e) => e.stopPropagation()}>
                    <div className="min-w-[160px] py-1">
                      <button
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[#2D2D2F]"
                        onClick={() => setTrailOpen(true)}
                      >
                        View trail
                      </button>
                      <button
                        className={"w-full text-left px-3 py-2 text-sm hover:bg-[#2D2D2F] flex items-center gap-2 " + (canDelete ? "text-red-400" : "text-[#8a8a8a] cursor-not-allowed")}
                        onClick={(e) => {
                          // Prevent the click from bubbling to the card or triggering underlying elements
                          e.preventDefault();
                          e.stopPropagation();
                          if (!canDelete) return;
                          // Ensure the trail dialog is not opened by any fall-through click
                          setTrailOpen(false);
                          deleteLoop({ loopId: loop.id, userId: loop.user_id });
                        }}
                        title={canDelete ? "Delete this post" : "You can only delete your own posts or as admin"}
                      >
                        <Trash2 size={14} />
                        Delete post
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </motion.div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 h-auto"
                    onClick={handleLike}
                    disabled={isLiking || isUnliking}
                  >
                    <Heart size={16} />
                    <span className="ml-1 text-xs">{loop.like_count || 0}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 h-auto opacity-50 cursor-not-allowed"
                    disabled={true}
                  >
                    <MessageCircle size={16} className="text-muted-foreground" />
                    <span className="ml-1 text-xs text-muted-foreground">--</span>
                  </Button>
                </div>
                <Button variant="ghost" size="sm" className="p-1 h-auto opacity-50 cursor-not-allowed" disabled={true}>
                  <MoreHorizontal size={16} className="text-muted-foreground" />
                </Button>
              </div>
            )}
          </AnimatePresence>
        </div>
        
        {isEngaged && (
          <>
            {/* Trail summary and quote chips */}
            <div className="mt-2 text-[11px] text-muted-foreground flex items-center flex-wrap gap-x-3 gap-y-1">
              <span>Original by <span className="font-medium">{loop.user.display_name || loop.user.username}</span></span>
              <span><Heart size={10} className="inline mr-1" />{formatCount(loop.like_count)}</span>
              <span><MessageCircle size={10} className="inline mr-1" />{formatCount(loop.comment_count)}</span>
              <span>↻ {formatCount(loop.reblog_count)}</span>
              <span>✍ {formatCount(loop.quote_count)}</span>
              <button className="underline hover:no-underline" onClick={(e) => { e.stopPropagation(); setTrailOpen(true); }}>View trail</button>
            </div>
            {annotations?.quoteChips?.length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {annotations.quoteChips.map((chip) => (
                  <div key={chip.id} className="text-[11px] px-2 py-1 rounded-full border bg-muted/40 max-w-[70%] truncate">
                    <span className="font-medium mr-1">@{chip.user?.username || 'unknown'}</span>
                    <span className="opacity-80">{chip.content}</span>
                  </div>
                ))}
                {annotations.quoteChips.length >= 2 && (
                  <button className="text-[11px] underline text-muted-foreground" onClick={(e) => { e.stopPropagation(); setTrailOpen(true); }}>+ more</button>
                )}
              </div>
            ) : null}
          </>
        )}
        
        {/* Comments section */}
        <AnimatePresence>
          {showComments && isEngaged && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3"
            >
              <LoopComments loopId={loop.id} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quote dialog */}
        <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>Quote this post</DialogTitle>
            </DialogHeader>
            <Textarea
              placeholder="Add your thoughts..."
              value={quoteText}
              onChange={(e) => setQuoteText(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setQuoteOpen(false)}>Cancel</Button>
              <Button
                onClick={() => { quote(loop.id, quoteText.trim()); setQuoteText(''); setQuoteOpen(false); }}
                disabled={!quoteText.trim()}
              >
                Post Quote
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Trail Lens dialog */}
        <TrailLensDialog postId={loop.id} open={trailOpen} onOpenChange={setTrailOpen} />
      </div>
    </motion.div>
  );
};

export default LoopCard;